import React from 'react';
import { useQuery } from 'react-query';
import * as api from 'config/api';
import * as consts from 'config/consts';
import { useRouting } from 'hooks/useRouting';
import { formatDate } from 'utils/formatter';
import {
  getChangeTotalAssetAmount as getChangeTotalAssetAmount_,
  getPrevAssetItem,
  getTargetPeriod,
  judgeAssetData,
} from 'pages/AssetTransition';
import Loading from 'components/commons/loading/Loading';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { getDate } from 'components/features/utils/converter';
import { amountToAmountColor } from 'components/features/utils/util';
import { addSignCommaFormat } from 'components/features/new-report/utils/formatter';

const AssetListLoading = () => {
  return (
    <div className="pt-4 px-4">
      <div className="text-lg font-bold">資産額の変動</div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
    </div>
  );
};

const AssetListInternal = ({ assets }: { assets: api.GetAssetResult }) => {
  const { pushRouting } = useRouting();
  const baseDay = getDate(assets.Items[0].Day);

  const getChangeTotalAssetAmount = (dateSelectIndex: number) => {
    const targetPeriod = getTargetPeriod(assets, dateSelectIndex, baseDay);

    // 対象期間のAsset情報の抽出
    const judgeAssetList = judgeAssetData(
      assets.Items,
      targetPeriod,
      dateSelectIndex
    );

    // 資産変動元のAsset情報を抽出
    const prevAssetItem = getPrevAssetItem(assets.Items, targetPeriod);

    const changeValue = getChangeTotalAssetAmount_(
      judgeAssetList,
      prevAssetItem
    );

    return { changeValue, targetPeriod };
  };

  const {
    changeValue: changeTotalAssetAmountTwoWeek,
  } = getChangeTotalAssetAmount(consts.ChartPeriodTypes.直近);
  const {
    changeValue: changeTotalAssetAmountMonth,
    targetPeriod: targetPeriodMonth,
  } = getChangeTotalAssetAmount(consts.ChartPeriodTypes.月);
  const { changeValue: changeTotalAssetAmountYear } = getChangeTotalAssetAmount(
    consts.ChartPeriodTypes.年
  );

  if (
    changeTotalAssetAmountTwoWeek === undefined ||
    changeTotalAssetAmountMonth === undefined ||
    changeTotalAssetAmountYear === undefined
  )
    return <AssetListLoading />;

  return (
    <>
      <div className="pt-4 px-4">
        <div className="text-lg font-bold mb-4">資産額の変動</div>
        <div className="flex flex-row py-1 text-sm font-bold text-bluegray-550 bg-gray-300">
          <div className="ml-8">日付</div>
          <div className="ml-auto mr-8">変動額</div>
        </div>
        <div
          className="grid items-center py-4 text-xs border-b border-bluegray-300"
          style={{ gridTemplateColumns: 'auto 1fr auto' }}>
          <div className="ml-8 font-bold">直近14日</div>
          <div
            className={`ml-auto mr-4 ${amountToAmountColor(
              changeTotalAssetAmountTwoWeek
            )}`}>
            <span className="mr-1 text-sm font-bold">
              {addSignCommaFormat(changeTotalAssetAmountTwoWeek, {
                numberOfDecimals: 0,
              })}
            </span>
            円
          </div>
        </div>
        <div
          className="grid items-center py-4 text-xs border-b border-bluegray-300"
          style={{ gridTemplateColumns: 'auto 1fr auto' }}>
          <div className="flex flex-col ml-8">
            <div className="font-bold">今月</div>
            <div>
              {formatDate(targetPeriodMonth.From, 'M')}/
              {formatDate(targetPeriodMonth.From, 'd')}~
              {formatDate(targetPeriodMonth.To, 'M')}/
              {formatDate(targetPeriodMonth.To, 'd')}
            </div>
          </div>
          <div
            className={`ml-auto mr-4 ${amountToAmountColor(
              changeTotalAssetAmountMonth
            )}`}>
            <span className="mr-1 text-sm font-bold">
              {addSignCommaFormat(changeTotalAssetAmountMonth, {
                numberOfDecimals: 0,
              })}
            </span>
            円
          </div>
        </div>
        <div
          className="grid items-center py-4 text-xs border-b border-bluegray-300"
          style={{ gridTemplateColumns: 'auto 1fr auto' }}>
          <div className="flex flex-col ml-8">
            <div className="font-bold">今年</div>
            <div>
              1月~
              {formatDate(yyyymmddToDate(assets.Items[0].Day.toString()), 'M')}
              月
            </div>
          </div>
          <div
            className={`ml-auto mr-4 ${amountToAmountColor(
              changeTotalAssetAmountYear
            )}`}>
            <span className="mr-1 text-sm font-bold">
              {addSignCommaFormat(changeTotalAssetAmountYear, {
                numberOfDecimals: 0,
              })}
            </span>
            円
          </div>
        </div>
        <div
          id="home_timeSeries"
          className="flex items-center justify-center text-base text-bluegray-500 border rounded-lg h-10 mt-4 cursor-pointer"
          onClick={(): void =>
            pushRouting({ path: `/assettransition` }, true, window.scrollY)
          }>
          時系列データで見る
        </div>
      </div>
    </>
  );
};
export const AssetsList = () => {
  const { data: assets } = useQuery('assetListGetAssetAPI', getAssetAPI, {
    refetchOnWindowFocus: false,
  });

  if (assets === undefined || assets === null) return <AssetListLoading />;

  // memo: 資産データが存在しない場合は項目自体を非表示
  if (assets.Item === null || assets.Items.length === 0) return <></>;

  return <AssetListInternal {...{ assets }} />;
};

const getAssetAPI = async () => {
  return await api.getAsset();
};
